import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, Slider } from "@mui/material";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { useTranslation } from "react-i18next";

const TemperatureAndDefrostSettings = ({ initialSettings, onSave, onChange, machineType }) => {
    const isPizza = machineType === "Pizza Vending Machine";
    const { mode } = useThemeContext();
    const { t } = useTranslation();

    const inputBoxColor = mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const btnColor = mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

    const [settings, setSettings] = useState({
        ...initialSettings,
        preHeatTemperature: initialSettings?.preHeatTemperature ?? 220,
        heatingTemperature: initialSettings?.heatingTemperature ?? 220,
        heatingTime: initialSettings?.heatingTime ?? 40,
    });

    const [temperatureRange, setTemperatureRange] = useState(
        isPizza
            ? initialSettings?.cabinetTemperature ?? 0
            : [initialSettings?.minTemperature ?? 0, initialSettings?.maxTemperature ?? 0]
    );

    useEffect(() => {
        if (initialSettings) {
            setSettings({
                ...initialSettings,
                preHeatTemperature: initialSettings?.preHeatTemperature ?? 220,
                heatingTemperature: initialSettings?.heatingTemperature ?? 220,
                heatingTime: initialSettings?.heatingTime ?? 40,
            });

            setTemperatureRange(
                isPizza
                    ? initialSettings?.cabinetTemperature ?? 0
                    : [initialSettings?.minTemperature ?? 0, initialSettings?.maxTemperature ?? 0]
            );
        }
    }, [initialSettings, machineType]);

    const handleUpdate = (key, value) => {
        const updatedSettings = { ...settings, [key]: value };
        setSettings(updatedSettings);
        if (onChange) onChange(updatedSettings);
    };

    const handleSave = () => {
        if (onSave) onSave(settings);
    };

    const renderTooltip = (value, min = 0, max = 100, suffix = "°C") => {
        const left = `calc(${((value - min) / (max - min)) * 100}% - 16px)`;
        return (
            <Box
                sx={{
                    position: "absolute",
                    top: "-45px",
                    left,
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Box sx={{ backgroundColor: "#DDD8F8", padding: "6px 10px", borderRadius: "8px", fontSize: "14px", color: "#000" }}>
                    {value}{suffix}
                </Box>
                <Box sx={{
                    width: 0,
                    height: 0,
                    borderLeft: "8px solid transparent",
                    borderRight: "8px solid transparent",
                    borderTop: "8px solid #DDD8F8",
                }} />
            </Box>
        );
    };

    const renderTickLabels = (min, max, interval = 20, suffix = "°C") => {
        const ticks = [];
        for (let i = min; i <= max; i += interval) {
            ticks.push(i);
        }

        return (
            <Box sx={tickLabelRowStyle}>
                {ticks.map((val) => (
                    <span key={val}>{val}{suffix}</span>
                ))}
            </Box>
        );
    };

    const CustomSliderBox = ({
        title,
        desc = '',
        min,
        max,
        step,
        value,
        onChange,
        tickSuffix = "°C",
        tickInterval = 5,
        colorType = "gradient",
        valueKey
    }) => {
        const [localValue, setLocalValue] = useState(value);

        useEffect(() => {
            setLocalValue(value);
        }, [value]);

        const handleDrag = (event, newValue) => {
            setLocalValue(newValue);
        };

        const handleCommit = (event, newValue) => {
            // 🔥 Call the provided onChange prop only after drag ends
            if (onChange) onChange(event, newValue);
        };

        return (
            <Box sx={sliderBoxStyle}>
                <Box sx={titleRowStyle}>
                    <Box>
                        <p className="machine-name-text">{title}</p>
                        <p className="add-machines">{desc}</p>
                    </Box>
                    <Box>
                        <button onClick={handleSave} className="save-btn" style={{ background: btnColor }}>
                            {t("membershipCards.save")}
                        </button>
                    </Box>
                </Box>

                <Box sx={{ position: "relative", mt: 8 }}>
                    <Slider
                        value={localValue}
                        onChange={handleDrag}
                        onChangeCommitted={handleCommit}
                        min={min}
                        max={max}
                        step={step}
                        valueLabelDisplay="off"
                        sx={sliderStyle(colorType, valueKey)}
                    />

                    {Array.isArray(localValue)
                        ? localValue.map(v => renderTooltip(v, min, max, tickSuffix))
                        : renderTooltip(localValue, min, max, tickSuffix)}
                </Box>

                {renderTickLabels(min, max, tickInterval, tickSuffix)}
            </Box>
        );
    };

    const DefrostSelect = ({ label, value, options, keyName }) => (
        <Box sx={{ flex: 1, minWidth: 140 }}>
            <Select
                value={value}
                onChange={(e) => handleUpdate(keyName, e.target.value)}
                fullWidth
                sx={{
                    backgroundColor: "#DDD8F8",
                    color: "#000",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                    "& .MuiSelect-icon": { color: "#000" },
                }}
            >
                {options.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
            </Select>
            <p className="add-machines" style={{ fontSize: "12px", marginTop: 8 }}>{label}</p>
        </Box>
    );

    const sliderBoxStyle = {
        backgroundColor: inputBoxColor,
        borderRadius: 2,
        p: { xs: 1, sm: 2, md: 3 },
        flex: "0 0 calc(50% - 16px)",
        minWidth: { xs: '100%', sm: '100%', md: 280 }
    };

    const titleRowStyle = {
        display: { xs: 'flex', md: "flex" },
        justifyContent: "space-between",
        mb: 3
    };

    const tickLabelRowStyle = {
        display: "flex",
        justifyContent: "space-between",
        fontSize: "12px",

        mt: 1,
        "@media (max-width: 350px)": {
            fontSize: "10px ",
        },
    };

    const sliderStyle = (colorType, valueKey) => {
        let trackBackground = "#BDBDBD"; // Default neutral

        if (colorType === "gradient") {
            trackBackground = "linear-gradient(90deg, #32A6F9 25.09%, #DD4242 76.25%)"; // Default blue-red
        }

        if (valueKey === "preHeatTemperature" || valueKey === "heatingTemperature") {
            trackBackground = "linear-gradient(90deg, #FF8178 25.09%, #DD4242 76.25%)"; // Red gradient
        }

        return {
            "& .MuiSlider-thumb": {
                backgroundColor: "#fff",
                width: 30,
                height: 30,
                boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                border: "none",
            },
            "& .MuiSlider-track, & .MuiSlider-rail": {
                background: trackBackground,
                height: 8,
                borderRadius: 4,
                border: "none",
            },
        };
    };

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, my: 4, mt: 8 }}>
            {!isPizza && (
                <CustomSliderBox
                    title={t("vendingMachines.temperatureSettings")}
                    desc={t("vendingMachines.temperatureSettingsText")}
                    min={-25}
                    max={25}
                    step={1}
                    value={temperatureRange}
                    onChange={(e, val) => {
                        const update = { minTemperature: val[0], maxTemperature: val[1] };
                        setTemperatureRange(val);
                        setSettings((prev) => ({ ...prev, ...update }));
                        if (onChange) onChange({ ...settings, ...update });
                    }}
                    tickSuffix="°C"
                    tickInterval={5}
                />
            )}
            {isPizza && (
                <>
                    <CustomSliderBox
                        title={t("vendingMachines.heatingTime")}
                        min={0}
                        max={300}
                        step={1}
                        value={settings.heatingTime}
                        onChange={(e, val) => handleUpdate("heatingTime", val)}
                        tickSuffix={` ${t('vendingMachines.sec')}`}
                        tickInterval={30}
                        colorType="default"
                    />
                    <CustomSliderBox
                        title={t("vendingMachines.cabinetTemperature")}
                        min={-10}
                        max={25}
                        step={1}
                        value={temperatureRange}
                        onChange={(e, val) => {
                            setTemperatureRange(val);
                            const update = { cabinetTemperature: val };
                            setSettings((prev) => ({ ...prev, ...update }));
                            if (onChange) onChange({ ...settings, ...update });
                        }}
                        tickSuffix="°C"
                        tickInterval={5}
                    />
                    <CustomSliderBox
                        title={t("vendingMachines.preHeatTemperature")}
                        min={0}
                        max={220}
                        step={1}
                        value={settings.preHeatTemperature}
                        onChange={(e, val) => handleUpdate("preHeatTemperature", val)}
                        tickSuffix="°C"
                        tickInterval={20}
                        valueKey="heatingTemperature"
                    />
                    <CustomSliderBox
                        title={t("vendingMachines.heatingTemperature")}
                        min={200}
                        max={300}
                        step={1}
                        value={settings.heatingTemperature}
                        onChange={(e, val) => handleUpdate("heatingTemperature", val)}
                        tickSuffix="°C"
                        tickInterval={20}
                        valueKey="heatingTemperature"
                    />
                </>
            )}

            {/* <Box sx={sliderBoxStyle}>
                <Box sx={titleRowStyle}>
                    <Box>
                        <p className="machine-name-text">{t("vendingMachines.defrostSettings")}</p>
                        <p className="add-machines">{t("vendingMachines.defrostSettingsText")}</p>
                    </Box>
                    <Box>
                        <button onClick={handleSave} className="save-btn" style={{ background: btnColor }}>
                            {t("membershipCards.save")}
                        </button>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <DefrostSelect
                        label={t("vendingMachines.defrostCycle")}
                        value={settings.defrostInterval}
                        keyName="defrostInterval"
                        options={[
                            { value: 60, label: t("vendingMachines.1Hour") },
                            { value: 120, label: t("vendingMachines.2Hour") },
                            { value: 180, label: t("vendingMachines.3Hour") },
                            { value: 240, label: t("vendingMachines.4Hour") }
                        ]}
                    />
                    <DefrostSelect
                        label={t("vendingMachines.defrostDuration")}
                        value={settings.defrostDuration}
                        keyName="defrostDuration"
                        options={[
                            { value: 10, label: t("vendingMachines.10Min") },
                            { value: 15, label: t("vendingMachines.15Min") },
                            { value: 20, label: t("vendingMachines.20Min") },
                            { value: 30, label: t("vendingMachines.30Min") }
                        ]}
                    />
                </Box>
            </Box> */}
        </Box>
    );
};

export default TemperatureAndDefrostSettings;
